import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Card } from "@redotech/redo-web/card";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { Input, groupInput, input } from "@redotech/ui/form";
import { useMutation } from "@tanstack/react-query";
import { memo, useContext } from "react";
import { RedoAdminRpcClientContext } from "../../../app/redo-admin-rpc-client-provider";
import { TeamContext } from "../../team";

export const importStripeInfoForm = groupInput({
  stripeCustomerId: input<string>(),
});

export const ImportStripeInfoCard = memo(function ImportStripeInfoCard({
  stripeCustomerIdInput,
}: {
  stripeCustomerIdInput: Input<string>;
}) {
  const team = useContext(TeamContext);
  const rpcClient = useContext(RedoAdminRpcClientContext);

  const importMutation = useMutation({
    mutationFn: async () => {
      if (!team || !rpcClient) {
        throw new Error("Team or RPC client not available");
      }
      if (!stripeCustomerIdInput.value.trim()) {
        throw new Error("Stripe Customer ID is required");
      }
      return rpcClient.importStripeInfo({
        teamId: team._id,
        stripeCustomerId: stripeCustomerIdInput.value.trim(),
      });
    },
    onSuccess: (response) => {
      if (response.success) {
        toast("Stripe information imported successfully", {
          variant: "success",
        });
        stripeCustomerIdInput.setValue("");
      } else {
        toast(response.error || "Failed to import Stripe information", {
          variant: "error",
        });
      }
    },
    onError: (error) => {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";
      toast(`Failed to import: ${errorMessage}`, { variant: "error" });
    },
  });

  if (!team || !rpcClient) {
    return null;
  }

  return (
    <Card title="Import Stripe Info">
      <Flex dir="column" gap="lg">
        <Text fontSize="sm">
          Import Stripe customer information and payment method for this team.
          This will retrieve the customer's default payment method from Stripe
          and associate it with the team.
        </Text>
        <Flex gap="md">
          <Flex dir="column" gap="xs" style={{ flex: 1 }}>
            <Text fontSize="sm" fontWeight="medium">
              Stripe Customer ID
            </Text>
            <input
              onChange={(e) => stripeCustomerIdInput.setValue(e.target.value)}
              placeholder="Customer ID"
              style={{
                padding: "var(--redo-spacing-md) var(--redo-spacing-lg)",
                border: "1px solid #ccc",
                borderRadius: "4px",
                fontSize: "14px",
              }}
              type="text"
              value={stripeCustomerIdInput.value}
            />
          </Flex>
          <Flex dir="column">
            <RedoButton
              disabled={importMutation.isPending}
              hierarchy="primary"
              onClick={() => importMutation.mutate()}
              size="sm"
              text={importMutation.isPending ? "Importing..." : "Import"}
            />
          </Flex>
        </Flex>
      </Flex>
    </Card>
  );
});
